import {
  gallonsToLiters,
  kiloBritishThermalUnitsPerSquareFootToMegajoulesPerSquareMeter,
  kiloBritishThermalUnitsToMegajoules,
  metricMillionBritishThermalUnitsPerSquareFootToCubicMetersPerSquareMeter,
  metricMillionBritishThermalUnitsToCubicMeters,
  squareFeetToSquareMeters,
} from "@/utils/calculations"

import type { MeasurementSystemCode } from "./measurementSystem"
import type { Token } from "./token"

type BaseUnitName =
  | "britishThermalUnit"
  | "carbonDioxide"
  | "carbonDioxideEquivalent"
  | "celsius"
  | "centumCubicFeet"
  | "cubicMeter"
  | "day"
  | "decatherm"
  | "fahrenheit"
  | "gallon"
  | "gigaJoule"
  | "kiloBritishThermalUnit"
  | "kilogram"
  | "kiloGallon"
  | "kilowatt"
  | "kilowattHour"
  | "liter"
  | "megajoule"
  | "megawatt"
  | "megawattHour"
  | "milleCubicFeet"
  | "metricMillionBritishThermalUnit"
  | "metricTon"
  | "pound"
  | "squareFoot"
  | "squareMeter"
  | "therm"
  | "thousandGallon"
  | "tonne"
  | "tonnes"
  | "wattHour"

type UnitSeparatorName = "of" | "per"

export interface UnitSeparator extends Token {
  displayValue: string
  id: UnitSeparatorName
}

export const unitSeparators = new Map<UnitSeparatorName, UnitSeparator>([
  [
    "of",
    {
      id: "of",
      displayValue: " ",
      tokenValue: "-of-",
    },
  ],
  [
    "per",
    {
      id: "per",
      displayValue: " / ",
      tokenValue: "-per-",
    },
  ],
])

export enum UnitName {
  BritishThermalUnit = "british-thermal-unit",
  CarbonDioxide = "carbon-dioxide",
  CarbonDioxideEquivalent = "carbon-dioxide-equivalent",
  Celsius = "celsius",
  CentumCubicFeet = "centum-cubic-feet",
  CubicMeter = "cubic-meter",
  Decatherm = "decatherm",
  Day = "day",
  Currency = "currency",
  CurrencyPerKilowattHour = "currency-per-kilowatt-hour",
  CurrencyPerMegawattHour = "currency-per-megawatt-hour",
  CurrencyPerSquareFoot = "currency-per-square-foot",
  CurrencyPerSquareMeter = "currency-per-square-meter",
  Fahrenheit = "fahrenheit",
  Gallon = "gallon",
  GigaJoule = "giga-joule",
  GigaJoulePerSquareFoot = "giga-joule-per-square-foot",
  KiloBritishThermalUnit = "kilo-british-thermal-unit",
  KiloBritishThermalUnitPerSquareFoot = "kilo-british-thermal-unit-per-square-foot",
  KiloGallon = "kilo-gallon",
  Kilogram = "kilogram",
  KilogramsOfCarbonDioxideEquivalent = "kilogram-of-carbon-dioxide-equivalent",
  KilogramsOfCarbonDioxideEquivalentPerKiloBritishThermalUnit = "kilogram-of-carbon-dioxide-equivalent-per-kilo-british-thermal-unit",
  KilogramsOfCarbonDioxideEquivalentPerKilowattHour = "kilogram-of-carbon-dioxide-equivalent-per-kilowatt-hour",
  KilogramsOfCarbonDioxideEquivalentPerSquareFoot = "kilogram-of-carbon-dioxide-equivalent-per-square-foot",
  Kilowatt = "kilowatt",
  KilowattHour = "kilowatt-hour",
  KilowattHourPerSquareFoot = "kilowatt-hour-per-square-foot",
  KilowattHourPerSquareMeter = "kilowatt-hour-per-square-meter",
  Liter = "liter",
  Megajoule = "megajoule",
  Megawatt = "megawatt",
  MegawattHour = "megawatt-hour",
  MegawattHourPerSquareFoot = "megawatt-hour-per-square-foot",
  MegawattHourPerSquareMeter = "megawatt-hour-per-square-meter",
  MilleCubicFeet = "mille-cubic-feet",
  MetricMillionBritishThermalUnits = "metric-million-british-thermal-units",
  MetricMillionBritishThermalUnitsPerSquareFoot = "metric-million-british-thermal-units-per-square-foot",
  MetricTon = "metric-ton",
  MetricTonsOfCarbonDioxideEquivalent = "metric-ton-of-carbon-dioxide-equivalent",
  MetricTonsOfCarbonDioxideEquivalentPerKilowattHour = "metric-ton-of-carbon-dioxide-equivalent-per-kilowatt-hour",
  MetricTonsOfCarbonDioxideEquivalentPerMegawattHour = "metric-ton-of-carbon-dioxide-equivalent-per-megawatt-hour",
  MetricTonsOfCarbonDioxideEquivalentPerSquareFoot = "metric-ton-of-carbon-dioxide-equivalent-per-square-foot",
  MetricTonsOfCarbonDioxideEquivalentPerSquareMeter = "metric-ton-of-carbon-dioxide-equivalent-per-square-meter",
  PerBritishThermalUnit = "-per-british-thermal-unit",
  PerCarbonDioxide = "-per-carbon-dioxide",
  PerCarbonDioxideEquivalent = "-per-carbon-dioxide-equivalent",
  PerCelsius = "-per-celsius",
  PerCentumCubicFeet = "-per-centum-cubic-feet",
  PerCubicMeter = "-per-cubic-meter",
  PerDay = "-per-day",
  PerDecatherm = "-per-decatherm",
  PerFahrenheit = "-per-fahrenheit",
  PerGallon = "-per-gallon",
  PerGigaJoule = "-per-giga-joule",
  PerKiloBritishThermalUnit = "-per-kilo-british-thermal-unit",
  PerKiloGallon = "-per-kilo-gallon",
  PerKilogram = "-per-kilogram",
  PerKilowatt = "-per-kilowatt",
  PerKilowattHour = "-per-kilowatt-hour",
  PerLiter = "-per-liter",
  PerMegajoule = "-per-megajoule",
  PerMegawatt = "-per-megawatt",
  PerMegawattHour = "-per-megawatt-hour",
  PerMilleCubicFeet = "-per-mille-cubic-feet",
  PerMetricMillionBritishThermalUnits = "-per-metric-million-british-thermal-units",
  PerMetricTon = "-per-metric-ton",
  PerPound = "-per-pound",
  PerSquareFoot = "-per-square-foot",
  PerSquareMeter = "-per-square-meter",
  PerTherm = "-per-therm",
  PerThousandGallon = "-per-thousand-gallon",
  PerTonne = "-per-tonne",
  PerTonnes = "-per-tonnes",
  PerWattHour = "-per-watt-hour",
  Pound = "pound",
  PoundsOfCarbonDioxideEquivalent = "pound-of-carbon-dioxide-equivalent",
  PoundsOfCarbonDioxideEquivalentPerKiloBritishThermalUnit = "pound-of-carbon-dioxide-equivalent-per-kilo-british-thermal-unit",
  PoundsOfCarbonDioxideEquivalentPerMegawattHour = "pound-of-carbon-dioxide-equivalent-per-megawatt-hour",
  PoundsOfCarbonDioxideEquivalentPerSquareFoot = "pound-of-carbon-dioxide-equivalent-per-square-foot",
  SquareFoot = "square-foot",
  SquareMeter = "square-meter",
  Therm = "therm",
  ThousandGallon = "thousand-gallon",
  Tonne = "tonne",
  Tonnes = "tonnes",
  TonnesOfCarbonDioxide = "tonne-of-carbon-dioxide",
  TonnesOfCarbonDioxideEquivalent = "tonne-of-carbon-dioxide-equivalent",
  WattHour = "watt-hour",
}

/**
 * Map of standard unit names to Japanese unit names
 */
export const jaUnitMap = new Map<UnitName, UnitName>([
  [UnitName.Gallon, UnitName.Liter],
  [UnitName.KiloBritishThermalUnit, UnitName.Megajoule],
  [UnitName.MetricMillionBritishThermalUnits, UnitName.CubicMeter],
  [UnitName.MetricTon, UnitName.Tonne],
  [UnitName.Pound, UnitName.Kilogram],
  [UnitName.SquareFoot, UnitName.SquareMeter],
])

/**
 * Map of standard unit names to Thai unit names
 */
export const thUnitMap = new Map<UnitName, UnitName>([
  [UnitName.Gallon, UnitName.Liter],
  [UnitName.KiloBritishThermalUnit, UnitName.Megajoule],
  [UnitName.MetricTon, UnitName.Tonne],
  [UnitName.Pound, UnitName.Kilogram],
  [UnitName.SquareFoot, UnitName.SquareMeter],
])

/**
 * Map of unit systems to unit maps
 */
export const languageToUnitMap = new Map<string, Map<UnitName, UnitName>>([
  ["ja", jaUnitMap],
  ["ja-JP", jaUnitMap],
  ["th", thUnitMap],
  ["th-TH", thUnitMap],
])

export type ConversionFunction = (value: number) => number

/**
 * Japanese map of unit names to conversion functions
 */
export const jaUnitConversionMap = new Map<UnitName, ConversionFunction>([
  [UnitName.Gallon, gallonsToLiters],
  [UnitName.KiloBritishThermalUnit, kiloBritishThermalUnitsToMegajoules],
  [
    UnitName.KiloBritishThermalUnitPerSquareFoot,
    kiloBritishThermalUnitsPerSquareFootToMegajoulesPerSquareMeter,
  ],
  [
    UnitName.MetricMillionBritishThermalUnits,
    metricMillionBritishThermalUnitsToCubicMeters,
  ],
  [
    UnitName.MetricMillionBritishThermalUnitsPerSquareFoot,
    metricMillionBritishThermalUnitsPerSquareFootToCubicMetersPerSquareMeter,
  ],
  [UnitName.SquareFoot, squareFeetToSquareMeters],
])

/**
 * Thai map of unit names to conversion functions
 */
export const thUnitConversionMap = new Map<UnitName, ConversionFunction>([
  [UnitName.Gallon, gallonsToLiters],
  [UnitName.KiloBritishThermalUnit, kiloBritishThermalUnitsToMegajoules],
  [
    UnitName.KiloBritishThermalUnitPerSquareFoot,
    kiloBritishThermalUnitsPerSquareFootToMegajoulesPerSquareMeter,
  ],
  [
    UnitName.MetricMillionBritishThermalUnitsPerSquareFoot,
    squareFeetToSquareMeters,
  ],
  [UnitName.SquareFoot, squareFeetToSquareMeters],
])

/**
 * Map of unit systems to conversion maps
 */
export const languageToUnitConversionMap = new Map<
  string,
  Map<UnitName, ConversionFunction>
>([
  ["ja", jaUnitConversionMap],
  ["ja-JP", jaUnitConversionMap],
  ["th", thUnitConversionMap],
  ["th-TH", thUnitConversionMap],
])

export const SIUnitNameToUSCSUnitNameMap = new Map<UnitName, UnitName>([
  [UnitName.Celsius, UnitName.Fahrenheit],
  [UnitName.Kilogram, UnitName.Pound],
  [UnitName.SquareMeter, UnitName.SquareFoot],
  [UnitName.CubicMeter, UnitName.Gallon],
])

export const USCSUnitNameToSIUnitNameMap = new Map<UnitName, UnitName>(
  Array.from(SIUnitNameToUSCSUnitNameMap).map(
    (pair: [UnitName, UnitName]): [UnitName, UnitName] =>
      [...pair].reverse() as [UnitName, UnitName]
  )
)

interface DisplayValue {
  shortDisplayValue: string
  longDisplayValue?: string
}

export interface DisplayValueMap {
  en: DisplayValue
  "en-US": DisplayValue
  ja: DisplayValue
  "ja-JP": DisplayValue
  th: DisplayValue
  "th-TH": DisplayValue
}

export interface Unit extends Token {
  displayValue: DisplayValueMap
  id: UnitName
  isECMAScriptSupported: boolean
  measurementSystem: MeasurementSystemCode | null
  shortDisplayValue: string
}

export const baseUnitShortDisplay: Record<BaseUnitName, string> = {
  britishThermalUnit: "Btu",
  carbonDioxide: "CO₂",
  carbonDioxideEquivalent: "CO₂e",
  celsius: "°C",
  centumCubicFeet: "CCF",
  cubicMeter: "m³",
  day: "day",
  decatherm: "Dth",
  fahrenheit: "°F",
  gallon: "gal",
  gigaJoule: "GJ",
  kiloBritishThermalUnit: "kBtu",
  kilogram: "kg",
  kiloGallon: "kgal",
  kilowatt: "kW",
  kilowattHour: "kWh",
  liter: "L",
  megajoule: "MJ",
  megawatt: "MW",
  megawattHour: "MWh",
  milleCubicFeet: "MCF",
  metricMillionBritishThermalUnit: "MMBtu",
  metricTon: "MT",
  pound: "lb",
  squareFoot: "ft²",
  squareMeter: "m²",
  therm: "thm",
  thousandGallon: "tgal",
  tonne: "t",
  tonnes: "tonnes",
  wattHour: "Wh",
}

export const thaiBaseUnitShortDisplay: Partial<Record<BaseUnitName, string>> = {
  cubicMeter: "ลบ.ม",
  kilogram: "กก.",
  liter: "ล.",
  squareFoot: "ฟุต²",
  squareMeter: "ตร.ม",
}

export const getThaiBaseUnitShortDisplay = (unit: BaseUnitName): string =>
  thaiBaseUnitShortDisplay[unit] ?? baseUnitShortDisplay[unit]

const uniformUnitDisplay = (shortDisplayValue: string): DisplayValueMap => ({
  en: { shortDisplayValue },
  "en-US": { shortDisplayValue },
  ja: { shortDisplayValue },
  "ja-JP": { shortDisplayValue },
  th: { shortDisplayValue },
  "th-TH": { shortDisplayValue },
})

const dataManagerUnit = (
  id: UnitName,
  shortDisplayValue: string,
  measurementSystem: MeasurementSystemCode
): Unit => ({
  displayValue: uniformUnitDisplay(shortDisplayValue),
  id,
  isECMAScriptSupported: false,
  measurementSystem,
  shortDisplayValue,
  tokenValue: id,
})

export const units = new Map<UnitName, Unit>([
  [
    UnitName.BritishThermalUnit,
    dataManagerUnit(
      UnitName.BritishThermalUnit,
      baseUnitShortDisplay.britishThermalUnit,
      "USCS"
    ),
  ],
  [
    UnitName.CentumCubicFeet,
    dataManagerUnit(
      UnitName.CentumCubicFeet,
      baseUnitShortDisplay.centumCubicFeet,
      "USCS"
    ),
  ],
  [
    UnitName.Decatherm,
    dataManagerUnit(UnitName.Decatherm, baseUnitShortDisplay.decatherm, "USCS"),
  ],
  [
    UnitName.KiloGallon,
    dataManagerUnit(
      UnitName.KiloGallon,
      baseUnitShortDisplay.kiloGallon,
      "USCS"
    ),
  ],
  [
    UnitName.MilleCubicFeet,
    dataManagerUnit(
      UnitName.MilleCubicFeet,
      baseUnitShortDisplay.milleCubicFeet,
      "USCS"
    ),
  ],
  [
    UnitName.ThousandGallon,
    dataManagerUnit(
      UnitName.ThousandGallon,
      baseUnitShortDisplay.thousandGallon,
      "USCS"
    ),
  ],
  [
    UnitName.WattHour,
    dataManagerUnit(UnitName.WattHour, baseUnitShortDisplay.wattHour, "SI"),
  ],
  [
    UnitName.Day,
    {
      displayValue: uniformUnitDisplay(baseUnitShortDisplay.day),
      id: UnitName.Day,
      isECMAScriptSupported: true,
      measurementSystem: null,
      shortDisplayValue: baseUnitShortDisplay.day,
      tokenValue: UnitName.Day,
    },
  ],
  [
    UnitName.CarbonDioxide,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.carbonDioxide,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.carbonDioxide,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.carbonDioxide,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.carbonDioxide,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.carbonDioxide,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.carbonDioxide,
        },
      },
      id: UnitName.CarbonDioxide,
      isECMAScriptSupported: false,
      measurementSystem: null,
      shortDisplayValue: baseUnitShortDisplay.carbonDioxide,
      tokenValue: UnitName.CarbonDioxide,
    },
  ],
  [
    UnitName.CarbonDioxideEquivalent,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.carbonDioxideEquivalent,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.carbonDioxideEquivalent,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.carbonDioxideEquivalent,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.carbonDioxideEquivalent,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.carbonDioxideEquivalent,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.carbonDioxideEquivalent,
        },
      },
      id: UnitName.CarbonDioxideEquivalent,
      isECMAScriptSupported: false,
      measurementSystem: null,
      shortDisplayValue: baseUnitShortDisplay.carbonDioxideEquivalent,
      tokenValue: UnitName.CarbonDioxideEquivalent,
    },
  ],
  [
    UnitName.Celsius,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.celsius,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.celsius,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.celsius,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.celsius,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.celsius,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.celsius,
        },
      },
      id: UnitName.Celsius,
      isECMAScriptSupported: true,
      measurementSystem: "SI",
      shortDisplayValue: baseUnitShortDisplay.celsius,
      tokenValue: UnitName.Celsius,
    },
  ],
  [
    UnitName.CubicMeter,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.cubicMeter,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.cubicMeter,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.cubicMeter,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.cubicMeter,
        },
        th: {
          shortDisplayValue: getThaiBaseUnitShortDisplay("cubicMeter"),
        },
        "th-TH": {
          shortDisplayValue: getThaiBaseUnitShortDisplay("cubicMeter"),
        },
      },
      id: UnitName.CubicMeter,
      isECMAScriptSupported: false,
      measurementSystem: "SI",
      shortDisplayValue: baseUnitShortDisplay.cubicMeter,
      tokenValue: UnitName.CubicMeter,
    },
  ],
  [
    UnitName.Currency,
    {
      displayValue: {
        en: {
          shortDisplayValue: "",
        },
        "en-US": {
          shortDisplayValue: "",
        },
        ja: {
          shortDisplayValue: "",
        },
        "ja-JP": {
          shortDisplayValue: "",
        },
        th: {
          shortDisplayValue: "",
        },
        "th-TH": {
          shortDisplayValue: "",
        },
      },
      id: UnitName.Currency,
      isECMAScriptSupported: false,
      measurementSystem: null,
      shortDisplayValue: "", // determined at runtime based on the locale
      tokenValue: UnitName.Currency,
    },
  ],
  [
    UnitName.Fahrenheit,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.fahrenheit,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.fahrenheit,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.fahrenheit,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.fahrenheit,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.fahrenheit,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.fahrenheit,
        },
      },
      id: UnitName.Fahrenheit,
      isECMAScriptSupported: true,
      measurementSystem: "USCS",
      shortDisplayValue: baseUnitShortDisplay.fahrenheit,
      tokenValue: UnitName.Fahrenheit,
    },
  ],
  [
    UnitName.Gallon,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.gallon,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.gallon,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.gallon,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.gallon,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.gallon,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.gallon,
        },
      },
      id: UnitName.Gallon,
      isECMAScriptSupported: false,
      measurementSystem: "USCS",
      shortDisplayValue: baseUnitShortDisplay.gallon,
      tokenValue: UnitName.Gallon,
    },
  ],
  [
    UnitName.GigaJoule,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.gigaJoule,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.gigaJoule,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.gigaJoule,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.gigaJoule,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.gigaJoule,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.gigaJoule,
        },
      },
      id: UnitName.GigaJoule,
      isECMAScriptSupported: false,
      measurementSystem: "SI",
      shortDisplayValue: baseUnitShortDisplay.gigaJoule,
      tokenValue: UnitName.GigaJoule,
    },
  ],
  [
    UnitName.KiloBritishThermalUnit,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.kiloBritishThermalUnit,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.kiloBritishThermalUnit,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.kiloBritishThermalUnit,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.kiloBritishThermalUnit,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.kiloBritishThermalUnit,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.kiloBritishThermalUnit,
        },
      },
      id: UnitName.KiloBritishThermalUnit,
      isECMAScriptSupported: false,
      measurementSystem: "BIS",
      shortDisplayValue: baseUnitShortDisplay.kiloBritishThermalUnit,
      tokenValue: UnitName.KiloBritishThermalUnit,
    },
  ],
  [
    UnitName.Kilogram,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.kilogram,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.kilogram,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.kilogram,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.kilogram,
        },
        th: {
          shortDisplayValue: getThaiBaseUnitShortDisplay("kilogram"),
        },
        "th-TH": {
          shortDisplayValue: getThaiBaseUnitShortDisplay("kilogram"),
        },
      },
      id: UnitName.Kilogram,
      isECMAScriptSupported: true,
      measurementSystem: "SI",
      shortDisplayValue: baseUnitShortDisplay.kilogram,
      tokenValue: UnitName.Kilogram,
    },
  ],
  [
    UnitName.Kilowatt,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.kilowatt,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.kilowatt,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.kilowatt,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.kilowatt,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.kilowatt,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.kilowatt,
        },
      },
      id: UnitName.Kilowatt,
      isECMAScriptSupported: false,
      measurementSystem: "SI",
      shortDisplayValue: baseUnitShortDisplay.kilowatt,
      tokenValue: "kilowatt",
    },
  ],
  [
    UnitName.KilowattHour,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.kilowattHour,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.kilowattHour,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.kilowattHour,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.kilowattHour,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.kilowattHour,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.kilowattHour,
        },
      },
      id: UnitName.KilowattHour,
      isECMAScriptSupported: false,
      measurementSystem: "SI",
      shortDisplayValue: baseUnitShortDisplay.kilowattHour,
      tokenValue: UnitName.KilowattHour,
    },
  ],
  [
    UnitName.Liter,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.liter,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.liter,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.liter,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.liter,
        },
        th: {
          shortDisplayValue: getThaiBaseUnitShortDisplay("liter"),
        },
        "th-TH": {
          shortDisplayValue: getThaiBaseUnitShortDisplay("liter"),
        },
      },
      id: UnitName.Liter,
      isECMAScriptSupported: true,
      measurementSystem: "SI",
      shortDisplayValue: baseUnitShortDisplay.liter,
      tokenValue: UnitName.Liter,
    },
  ],
  [
    UnitName.Megajoule,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.megajoule,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.megajoule,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.megajoule,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.megajoule,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.megajoule,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.megajoule,
        },
      },
      id: UnitName.Megajoule,
      isECMAScriptSupported: false,
      measurementSystem: "SI",
      shortDisplayValue: baseUnitShortDisplay.megajoule,
      tokenValue: UnitName.Megajoule,
    },
  ],
  [
    UnitName.Megawatt,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.megawatt,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.megawatt,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.megawatt,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.megawatt,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.megawatt,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.megawatt,
        },
      },
      id: UnitName.Megawatt,
      isECMAScriptSupported: false,
      measurementSystem: "SI",
      shortDisplayValue: baseUnitShortDisplay.megawatt,
      tokenValue: UnitName.Megawatt,
    },
  ],
  [
    UnitName.MegawattHour,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.megawattHour,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.megawattHour,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.megawattHour,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.megawattHour,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.megawattHour,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.megawattHour,
        },
      },
      id: UnitName.MegawattHour,
      isECMAScriptSupported: false,
      measurementSystem: "SI",
      shortDisplayValue: baseUnitShortDisplay.megawattHour,
      tokenValue: UnitName.MegawattHour,
    },
  ],
  [
    UnitName.MetricMillionBritishThermalUnits,
    {
      displayValue: {
        en: {
          shortDisplayValue:
            baseUnitShortDisplay.metricMillionBritishThermalUnit,
        },
        "en-US": {
          shortDisplayValue:
            baseUnitShortDisplay.metricMillionBritishThermalUnit,
        },
        ja: {
          shortDisplayValue:
            baseUnitShortDisplay.metricMillionBritishThermalUnit,
        },
        "ja-JP": {
          shortDisplayValue:
            baseUnitShortDisplay.metricMillionBritishThermalUnit,
        },
        th: {
          shortDisplayValue:
            baseUnitShortDisplay.metricMillionBritishThermalUnit,
        },
        "th-TH": {
          shortDisplayValue:
            baseUnitShortDisplay.metricMillionBritishThermalUnit,
        },
      },
      id: UnitName.MetricMillionBritishThermalUnits,
      isECMAScriptSupported: false,
      measurementSystem: "BIS",
      shortDisplayValue: baseUnitShortDisplay.metricMillionBritishThermalUnit,
      tokenValue: UnitName.MetricMillionBritishThermalUnits,
    },
  ],
  [
    UnitName.MetricTon,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.metricTon,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.metricTon,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.metricTon,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.metricTon,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.metricTon,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.metricTon,
        },
      },
      id: UnitName.MetricTon,
      isECMAScriptSupported: false,
      measurementSystem: "SI",
      shortDisplayValue: baseUnitShortDisplay.metricTon,
      tokenValue: UnitName.MetricTon,
    },
  ],
  [
    UnitName.Pound,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.pound,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.pound,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.pound,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.pound,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.pound,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.pound,
        },
      },
      id: UnitName.Pound,
      isECMAScriptSupported: true,
      measurementSystem: "USCS",
      shortDisplayValue: baseUnitShortDisplay.pound,
      tokenValue: UnitName.Pound,
    },
  ],
  [
    UnitName.SquareFoot,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.squareFoot,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.squareFoot,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.squareFoot,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.squareFoot,
        },
        th: {
          shortDisplayValue: getThaiBaseUnitShortDisplay("squareFoot"),
        },
        "th-TH": {
          shortDisplayValue: getThaiBaseUnitShortDisplay("squareFoot"),
        },
      },
      id: UnitName.SquareFoot,
      isECMAScriptSupported: false,
      measurementSystem: "USCS",
      shortDisplayValue: baseUnitShortDisplay.squareFoot,
      tokenValue: UnitName.SquareFoot,
    },
  ],
  [
    UnitName.SquareMeter,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.squareMeter,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.squareMeter,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.squareMeter,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.squareMeter,
        },
        th: {
          shortDisplayValue: getThaiBaseUnitShortDisplay("squareMeter"),
        },
        "th-TH": {
          shortDisplayValue: getThaiBaseUnitShortDisplay("squareMeter"),
        },
      },
      id: UnitName.SquareMeter,
      isECMAScriptSupported: false,
      measurementSystem: "SI",
      shortDisplayValue: baseUnitShortDisplay.squareMeter,
      tokenValue: UnitName.SquareMeter,
    },
  ],
  [
    UnitName.Therm,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.therm,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.therm,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.therm,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.therm,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.therm,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.therm,
        },
      },
      id: UnitName.Therm,
      isECMAScriptSupported: false,
      measurementSystem: "BIS",
      shortDisplayValue: baseUnitShortDisplay.therm,
      tokenValue: UnitName.Therm,
    },
  ],
  [
    UnitName.Tonne,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.tonne,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.tonne,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.tonne,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.tonne,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.tonne,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.tonne,
        },
      },
      id: UnitName.Tonne,
      isECMAScriptSupported: false,
      measurementSystem: "SI",
      shortDisplayValue: baseUnitShortDisplay.tonne,
      tokenValue: UnitName.Tonne,
    },
  ],
  [
    UnitName.Tonnes,
    {
      displayValue: {
        en: {
          shortDisplayValue: baseUnitShortDisplay.tonnes,
        },
        "en-US": {
          shortDisplayValue: baseUnitShortDisplay.tonnes,
        },
        ja: {
          shortDisplayValue: baseUnitShortDisplay.tonnes,
        },
        "ja-JP": {
          shortDisplayValue: baseUnitShortDisplay.tonnes,
        },
        th: {
          shortDisplayValue: baseUnitShortDisplay.tonnes,
        },
        "th-TH": {
          shortDisplayValue: baseUnitShortDisplay.tonnes,
        },
      },
      id: UnitName.Tonnes,
      isECMAScriptSupported: false,
      measurementSystem: "SI",
      shortDisplayValue: baseUnitShortDisplay.tonnes,
      tokenValue: UnitName.Tonnes,
    },
  ],
])
